import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import {
  isLossAversionCard,
  type RestMultiRowSurveyCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import { type RestLossAversionCard } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import {
  CtaProcessingOption,
  RestModalAction,
  RestModalTemplates,
  type RestModalEmailSuccessModal,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type EventObject, type ExecuteEventsHandler } from '@bbkFunnel/events';

type LaConfirmation =
  | { status: 'none' | 'invalid' }
  | {
      status: 'ready';
      offerCard: RestLossAversionCard;
      offerCta: RestModalForwards;
      confirmationModal: RestModalEmailSuccessModal;
    };

export const LA_CONFIRMATION_UNAVAILABLE =
  'The confirmation modal is unavailable. Please try again later.';

// A dedicated confirmation has exactly one incoming CTA, which owns its
// processing identity.
export const resolveLaConfirmation = (
  cards: RestCard[],
  name?: string
): LaConfirmation => {
  if (!name) return { status: 'none' };
  const owners = cards.flatMap((card) =>
    collectForwards(card)
      .filter(({ value }) => value?.modal === name)
      .map(({ value: cta }) => ({ card, cta }))
  );
  if (
    !owners.some(
      ({ cta }) => cta.action === RestModalAction.open_offer_confirmation
    )
  )
    return { status: 'none' };
  const owner = owners[0];
  const targets = cards.filter((card) => card.name === name);
  const modal = targets[0];
  if (
    owners.length !== 1 ||
    !owner ||
    !isLossAversionCard(owner.card) ||
    owner.cta.cta_details_list?.some(
      (details) =>
        details.processing_option === CtaProcessingOption.chargebee_checkout
    ) ||
    targets.length !== 1 ||
    modal?.template !== RestModalTemplates.modal_email_success ||
    modal.requires?.forwards?.action !== RestModalAction.link
  )
    return { status: 'invalid' };
  return {
    status: 'ready',
    offerCard: owner.card,
    offerCta: owner.cta,
    confirmationModal: modal,
  };
};

type ConfirmationContext = Pick<
  EventObject,
  'session' | 'version' | 'app_id' | 'user_id'
> & {
  surveyState: RestMultiRowSurveyCard['provides'];
  isLifecycle?: boolean;
  fallbackCtaId?: string;
};

// Acceptance belongs to the original LA; navigation belongs to its confirmation.
// Keep both in the ordinary queue, without changing transport ordering.
export const buildLaConfirmationSubmission = (
  {
    offerCard,
    offerCta,
    confirmationModal,
  }: Extract<LaConfirmation, { status: 'ready' }>,
  returnAction: RestModalForwards,
  { surveyState, isLifecycle, fallbackCtaId, ...identity }: ConfirmationContext
): Parameters<ExecuteEventsHandler>[0] => {
  const messageVal = offerCta.sync?.message;
  const commonKeys = {
    ...identity,
    cta_ids: offerCta.cta_details_list?.map((cta) => cta.cta_id) ?? [
      fallbackCtaId,
    ],
    cta_id: offerCta.cta_details_list?.[0]?.cta_id ?? fallbackCtaId,
  };
  const offerKeys = {
    ...commonKeys,
    id: offerCard.id,
    name: offerCard.name,
    card_version: offerCard.version,
  };
  const syncKeys = {
    ...commonKeys,
    direction: 'forwards' as const,
    survey_state: surveyState,
  };
  const offerForm = {
    sync: offerCta.sync,
    message: messageVal,
    survey_state: surveyState,
  };
  return {
    synchronous: true,
    events: [
      {
        ...offerKeys,
        event: RestModalAction.accepted_la_offer,
        properties: {
          sync: offerCta.sync,
          message: messageVal,
          survey_state: surveyState,
        },
      },
    ],
    syncs: [
      {
        ...syncKeys,
        ...offerKeys,
        validation: offerCta.validation,
        type: offerCta.type,
        action: RestModalAction.accept_offer,
        form: offerForm,
      },
      {
        ...syncKeys,
        id: confirmationModal.id,
        name: confirmationModal.name,
        card_version: confirmationModal.version,
        validation: returnAction.validation,
        action: RestModalAction.link,
        form: { ...returnAction.sync, message: messageVal },
        url: isLifecycle ? '?close_iframe' : returnAction.url,
        lco_redirect_url: returnAction.url,
        isCheckoutProcessing:
          returnAction.cta_details_list?.[0]?.processing_option ===
          CtaProcessingOption.chargebee_checkout,
      },
    ],
  };
};
